import { Quill } from 'react-quill';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import store from 'redux/store';
import * as autocompleteStore from 'redux/autocomplete';
import { KEYS } from 'utils/editorHelper';
import mapDispatchToMethods from 'utils/mapDispatchToMethods';
import Autocomplete from 'components/Autocomplete';
import lizzyApi from 'lizzyApiAdapter';

const Module = Quill.import('core/module');

export default class TextPredictionModule extends Module {
  constructor(quill, options) {
    super(quill, options);
    this.quill = quill;
    this.options = options;
    this.container = document.createElement('div');

    this.quill.container.appendChild(this.container);

    this.lastSelection = null;
    this.currentLine = null;

    this.keyPressDelta = 300;
    this.lastDoubleTabTime = 0;
    this.tabPressCount = 1;
    this.tabPressTimeout = null;
    this.reactRoot = null;
    this.reactAcRoot = null;
    this.extraLines = [];

    this.onUpdateAutoCompleteData = this.onUpdateAutoCompleteData.bind(this);
    this.onClearAutoCompleteData = this.onClearAutoCompleteData.bind(this);
    this.clearSuggestion = this.clearSuggestion.bind(this);
    this.onTextChange = this.onTextChange.bind(this);
    this.handleSelectionChange = this.handleSelectionChange.bind(this);
    this.clearSuggestion = this.clearSuggestion.bind(this);

    mapDispatchToMethods(this, autocompleteStore, store.dispatch, [
      'setContainerData',
      'setIsOpen',
      'setTextSuggestion',
      'highlightOption',
      'setOptions',
      'setAutocompleteData',
      'setIsLoading',
      'startStreamAutocompleteSentence',
      'processStreamParts',
      'finishStreamAutocomplete',
      'setNextWordsAsTextSuggestion',
      'clearStreamParts',
      'startStreamAutocompleteClause',
      'streamAutocompleFailure',
      'setIsSuggestionElementExists'
    ]);
    
    this.quill.on('text-change', this.onTextChange);
    this.quill.on('selection-change', this.handleSelectionChange);

    this.quill.keyboard.addBinding({
      key: 'Tab',
      handler: this.handleTab.bind(this)
    });

    this.quill.keyboard.addBinding({
      key: 'down',
      handler: this.handleArrowDown.bind(this)
    });

    this.quill.keyboard.addBinding({
      key: 'up',
      handler: this.handleArrowUp.bind(this)
    });

    this.quill.keyboard.addBinding({
      key: 'Enter',
      handler: this.handleEnter.bind(this)
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.handleEscape();
      }
    });

    this.quill.keyboard.bindings[KEYS.TAB].unshift(
      this.quill.keyboard.bindings[KEYS.TAB].pop()
    );

    this.quill.keyboard.bindings[KEYS.ENTER].unshift(
      this.quill.keyboard.bindings[KEYS.ENTER].pop()
    );
  }

  onMount() {
    this.renderReactTimeout && clearTimeout(this.renderReactTimeout);

    lizzyApi.on('updateAutoCompleteData', this.onUpdateAutoCompleteData);
    lizzyApi.on('clearAutoCompleteData', this.onClearAutoCompleteData);
    
    lizzyApi.assignCallbacks({
      onGetSentences: (lines) => this.getSentences(lines),
      onGetCurrentClause: () => this.getClause(),
      onGetDocument: (hash) => this.getDocument(hash),
    });

    document.addEventListener('click', this.clearSuggestion);

    this.lastSelection = this.quill.getSelection();

    // It should be wrapped againg into the <Provider> component
    // as it's another part of the current react-app
    const AutocompleteComponent = () => {
      return (
          <>
            <Autocomplete
              editorContainerEl={this.quill.container}
              onOptionClick={(event, option) => this.handleOptionClick(event, option)}
              onCloseClick={() => this.handleEscape()}
              onLinesCountChange={(lines) => this.autocompleteShiftLines(lines)}
            />
          </>
      );
    };

    if (!this.reactRoot) {
      this.reactRoot = ReactDOM.createRoot(this.container);
    }
    this.reactRoot.render(
      <Provider store={store}>
        <AutocompleteComponent />
      </Provider>
    );
  }

  onUnmount() {
    lizzyApi.off('updateAutoCompleteData', this.onUpdateAutoCompleteData);
    lizzyApi.off('clearAutoCompleteData', this.onClearAutoCompleteData);

    document.removeEventListener('click', this.clearSuggestion);

    this.renderReactTimeout = setTimeout(() => {
      this.reactRoot && this.reactRoot.unmount();
      this.reactRoot = null;
    }, 200);
  }

  onUpdateAutoCompleteData({ result }) {
    this.setAutocompleteData({
      items: result.items,
      sentence: result.sentence,
      clause: result.clause,
      nextwordpart: result.nextwordpart,
      nextwords: result.nextwords,
      isOpen: !this.hasTextAfterCursor
    });
  }

  onClearAutoCompleteData() {
    this.setAutocompleteData({
      items: [],
      sentence: '',
      clause: '',
    });
  }

  get state() {
    return store.getState();
  }

  get textSuggestion() {
    return this.state.autocomplete.textSuggestion;
  }

  get nextwords() {
    return this.state.autocomplete.nextwords;
  }

  get nextwordpart() {
    return this.state.autocomplete.nextwordpart;
  }

  get isAutocompleteOpen() {
    return this.state.autocomplete.isOpen;
  }
  
  get autocompleteOptions() {
    return this.state.autocomplete.options;
  }

  get isAutocompleteDropdownDisplayed() {
    return this.isAutocompleteOpen && this.autocompleteOptions.length;
  }

  get autocompleteMode() {
    return this.state.autocomplete.mode;
  }
  
  get sentence() {
    return this.state.autocomplete.sentence;
  }

  get clause() {
    return this.state.autocomplete.clause;
  }

  get streamParts() {
    return this.state.autocomplete.streamParts;
  }

  get hasTextAfterCursor() {
    return this.state.autocomplete.containerData.hasTextAfterCursor;
  }

  get highlightedOption() {
    const options = this.state.autocomplete.options;
    const dropdownHighlightIndex = this.state.autocomplete.dropdownHighlightIndex;

    if (options[dropdownHighlightIndex]) {
      return options[dropdownHighlightIndex];
    }

    return null;
  }

  get editorText() {
    return this.state.editor.currentContentHtml;
  }

  get editorSelection() {
    let selection = this.quill.getSelection();
    return selection || this.lastSelection;
  }
  
  updateContainerData(options = {}) {
    const bounds = this.quill.getBounds(this.lastSelection.index);
    this.setContainerData({
      ...bounds,
      ...options,
    });
  }

  getCurrentCarretText() {
    const [line, offset] = this.quill.getLine(this.lastSelection.index);

    const text = line.domNode.textContent;
    const endOfLineIndex = text.length - offset;
    const lineIndex = this.quill.getIndex(line);
    const toCaret = this.quill.getText(lineIndex, this.lastSelection.index - lineIndex);
    const fromCaret = this.quill.getText(this.lastSelection.index, endOfLineIndex);

    return {
      toCaret,
      fromCaret,
    };
  }

  onTextChange(delta, oldContents, source) {
    // this.removeTextSuggestion();

    this.lastSelection = this.quill.getSelection();

    if (this.lastSelection && source === 'user') {
      this.clearSuggestion();

      this.currentLine = this.quill.getLine(this.lastSelection.index);

      if (this.currentLine) {
        const [line, offset] = this.currentLine;
        const text = line.domNode.textContent;
        const endOfLineIndex = text.length - offset;
        const lineIndex = this.quill.getIndex(line);
        const textBeforeCursor = this.quill.getText(lineIndex, this.lastSelection.index - lineIndex);
        const textAfterCursor = this.quill.getText(this.lastSelection.index, endOfLineIndex);
        const hasTextAfterCursor = !!textAfterCursor.trim().length;
        // const allTextAfterCursor = this.quill.getText(this.lastSelection.index, this.quill.getLength());

        if (!hasTextAfterCursor) {
          lizzyApi.emit('updateCurrentLine', {
            toCaret: textBeforeCursor,
            fromCaret: textAfterCursor
          });
        }

        this.updateContainerData({ hasTextAfterCursor });
      }
    }
  }

  handleSelectionChange(range, oldRange, source) {
    if (range) {
      const topOffest = this.quill.getBounds(range.index);

      if (source === 'user') {
        this.clearSuggestion();
      }

      this.lastSelection = this.quill.getSelection();

      this.updateHasTextAfterCursor();

      this.setContainerData({
        linePosition: topOffest
      });
    }
  }

  handleArrowDown(range) {
    if (this.isAutocompleteDropdownDisplayed) {
      this.highlightOption('down');

      return false;
    }

    return true;
  }

  handleArrowUp(range) {
    if (this.isAutocompleteDropdownDisplayed) {
      this.highlightOption('up');

      return false;
    }

    return true;
  }

  handleEnter() {
    if (this.isAutocompleteDropdownDisplayed) {
      this.insertTextFromSuggestion(this.highlightedOption.text);
      return false;
    }

    return true;
  }

  handleOptionClick(event, text) {
    event.stopPropagation();
    event.preventDefault();

    this.quill.focus();

    this.insertTextFromSuggestion(text);
  }

  handleTab() {
    this.tabPressTimeout && clearTimeout(this.tabPressTimeout);
    let currentKeypressTime = new Date();
    const diff = currentKeypressTime - this.lastDoubleTabTime;

    if (diff <= this.keyPressDelta) {
      this.tabPressCount += 1;

      this.tabPressTimeout = setTimeout(() => {
        switch (this.tabPressCount) {
          case 2:
            this.handleDoubleTab();
            break;
          case 3:
            this.handleTripleTab();
            break;
          default:
            break;
        }
      }, diff + 50);
    } else {
      this.tabPressCount = 1;

      this.tabPressTimeout = setTimeout(() => {
        this.handleSingleTab();
      }, this.keyPressDelta);
    }

    this.lastDoubleTabTime = currentKeypressTime;
  }

  handleSingleTab() {
    if (this.suggestionElement) {
      this.insertInlineSuggestion();

      return;
    }

    if (this.streamParts.length) {
      this.insertTextFromSuggestion(this.textSuggestion);
    } else if (this.nextwords) {
      this.insertTextFromSuggestion(this.nextwords);
    }
    this.clearStreamParts();
  }

  handleDoubleTab() {
    if (!this.editorText.length) {
      return;
    }

    this.updateContainerData();
    this.startStreamAutocompleteSentence();
    
    lizzyApi.completeSentence((result) => {
      if (!result.valid) {
        this.streamAutocompleFailure();
      }

      this.processStreamParts(result.text);

      if (result.eof) {
        this.finishStreamAutocomplete();
      }
    });
  }
  
  handleTripleTab() {
    if (!this.editorText.length) {
      return;
    }

    this.updateContainerData();
    this.startStreamAutocompleteClause();

    lizzyApi.completeClause((result) => {
      if (!result.valid) {
        this.streamAutocompleFailure();
      }

      this.processStreamParts(result.text);

      if (result.eof || !result.valid) {
        this.finishStreamAutocomplete();
      }
    });
  }

  handleEscape(event) {
    // if (this.isAutocompleteDropdownDisplayed) {
    this.clearSuggestion();
    // }
  }

  insertTextFromSuggestion(suggestionText, skipLastWordReplace) {
    this.clearSuggestion();

    const [line, offset] = this.quill.getLine(this.editorSelection.index);

    const lineIndex = this.quill.getIndex(line);
    const textBeforeCursor = this.quill.getText(lineIndex, this.editorSelection.index - lineIndex);

    const endOfLineOffset = textBeforeCursor.length - offset;
    const lastWordOffset = textBeforeCursor.lastIndexOf(' ') + 1 - offset;

    const endOfLineIndex = this.editorSelection.index + endOfLineOffset;
    const lastWordIndex = this.editorSelection.index + lastWordOffset;

    this.quill.insertText(this.editorSelection.index, suggestionText, 'plain', Quill.sources.USER);

    if (!skipLastWordReplace) {
      this.quill.deleteText(lastWordIndex, endOfLineIndex - lastWordIndex, Quill.sources.USER);
    }
  }

  insertInlineSuggestion() {
    this.removeTextSuggestion();
    const textSuggestion = this.textSuggestion;
    this.quill.insertText(this.editorSelection.index, textSuggestion, 'plain', Quill.sources.USER);
  }

  insertTextSuggestion(value, isEnd) {
    if (!this.suggestionElement && value) {
      try {
        this.quill.insertEmbed(this.editorSelection.index, 'suggestion', {
          value,
          getNode: ({node, textNode}) => {
            this.suggestionElement = node;
            this.setIsSuggestionElementExists(true);
          }
        }, Quill.sources.API);
      } catch (error) {
        console.warn(error.message);
      }
    }

    if (value && this.suggestionElement) {
      this.suggestionElement.textContent = value;
    }

    // this.quill.setSelection({ index: this.editorSelection.index + value.length, length: 0 }, 'api');
  }

  // updateSuggestionElement() {
  //   if (this.editorSelection) {
  //     const [line] = this.quill.getLine(this.editorSelection.index);
  //     this.suggestionElement = Array.from(line.domNode.childNodes).find((node) => node.className === 'suggestion');
  //     this.quill.setSelection(this.editorSelection, Quill.sources.SILENT);
  //   }
  // }

  removeTextSuggestion() {
    this.setIsSuggestionElementExists(false);
    this.suggestionElement && this.suggestionElement.remove();
    this.suggestionElement = null;
  }

  getDocument() {
    const lines = this.quill.getLines(0, this.quill.getLength());

    return lines.map((line, index) => {
      const lineIndex = this.quill.getIndex(line);
      const lineText = this.quill.getText(
        lineIndex,
        line.domNode.textContent.length
      );

      return lineText;
    });
  }

  getClause() {
    return this.getCurrentCarretText();
  }

  getSentences(linesNumber) {
    let range = this.quill.getSelection();
    range = range || this.lastSelection;
    
    const [line, offset] = this.quill.getLine(range.index);
    const allLines = this.quill.getLines(range.index, this.quill.getLength());

    if (linesNumber === 0) {
      const lineIndex = this.quill.getIndex(line);
      const textBeforeCursor = this.quill.getText(lineIndex, range.index - lineIndex);

      return [textBeforeCursor];
    } else if (linesNumber > 0) {
      return allLines.slice(0, Math.abs(linesNumber) + 1).map((line, index) => {
        const lineIndex = this.quill.getIndex(line);
        const lineText = this.quill.getText(
          index === 0 ? lineIndex + offset : lineIndex,
          index === 0 ? line.domNode.textContent.length - offset : line.domNode.textContent.length
        );
        return lineText;
      });
    } else if (linesNumber < 0) {
      const lines = this.quill.getLines(0, range.index);

      return lines.reverse().slice(0, Math.abs(linesNumber) + 1).map((line, index) => {
        const lineIndex = this.quill.getIndex(line);
        const lineText = this.quill.getText(
          lineIndex,
          index === 0 ? offset : line.domNode.textContent.length
        );
        return lineText;
      });
    }
  }

  autocompleteShiftLines(lines) {
    if (lines > 1) {
      const delta = this.quill.insertText(this.editorSelection.index, '\n', 'plain', 'silent');
      this.quill.setSelection(this.editorSelection.index - 1, '\n', 'plain', 'silent');
      this.extraLines.push(delta);
    }
  }

  clearSuggestion() {
    this.setIsOpen(false);
    this.setTextSuggestion('');
    this.highlightOption(null);
    this.setOptions();
    this.clearExtraLines();
  }

  clearExtraLines() {
    this.extraLines.reverse().forEach((delta) => {
      this.quill.deleteText(delta.ops[0].retain, 1, 'api');
    });

    this.extraLines = [];
  }

  updateHasTextAfterCursor() {
    const currentLine = this.quill.getLine(this.editorSelection.index);

    const [line, offset] = currentLine;
    const text = line.domNode.textContent;
    const endOfLineIndex = text.length - offset;
    const lineIndex = this.quill.getIndex(line);
    const textBeforeCursor = this.quill.getText(lineIndex, this.lastSelection.index - lineIndex);
    const textAfterCursor = this.quill.getText(this.lastSelection.index, endOfLineIndex);

    this.updateContainerData({
      hasTextAfterCursor: !!textAfterCursor.trim().length
    });
  }
}